import React, { PropsWithChildren } from 'react';

import { getLoginLiteUrlWithRedirect, getLoginUrlWithRedirect } from '@opendoor/auth-fe';
import { ThemedLite } from '@opendoor/bricks/theme/ODThemeLite';

import { RAILS_ENV } from 'shared/helpers/globals';

import { LoadingPage } from './components/LoadingPage';
import { UnauthenticatedError } from './Error';
import navigationHelpers from './navigationHelpers';

/**
 * Implements componentDidCatch to track the error in observability to handle unauthenticated errors.
 *
 * Rethrows the error if it is not an UnauthenticatedError.
 */
export class UnauthenticatedCustomerBoundary extends React.Component<
  PropsWithChildren,
  { isUnauthenticated: boolean; error: UnauthenticatedError | undefined }
> {
  state = {
    isUnauthenticated: false,
    error: undefined,
  };

  /** Calls super */
  constructor(props: PropsWithChildren) {
    super(props);
  }

  /** @throws OD Team Boundary Error */
  componentDidCatch(error: UnauthenticatedError | Error) {
    if (error instanceof UnauthenticatedError) {
      this.setState({ isUnauthenticated: true, error });
    } else {
      // Just forward error has already been caught once by boundary
      throw error;
    }
  }

  render() {
    if (this.state.isUnauthenticated && this.state.error) {
      const error = this.state.error as UnauthenticatedError;
      let label = 'login';
      let redirectUrl = getLoginUrlWithRedirect(RAILS_ENV);
      if (error.accessToken) {
        // If there is no user but there is an `access_token` param in the URL, we will
        // redirect to the login lite url (opendoor.com/token)
        label = 'login-login-lite';
        redirectUrl = getLoginLiteUrlWithRedirect(RAILS_ENV, error.accessToken);
      } else if (error.emailPrefill) {
        // If the user is not authenticated we redirect to the login page.
        // If there is an `emailPrefill` param in the URL, we add it as a query param
        // to pre-populate the log-in/sign-up form
        label = 'login-email-prefill';
        redirectUrl = getLoginUrlWithRedirect(RAILS_ENV, error.emailPrefill);
      }
      // Redirect customers to the login page if they are not authenticated
      navigationHelpers.hardNavReplace(redirectUrl, { label, data: { ...error } });
      return (
        <ThemedLite theme="novo">
          <LoadingPage />
        </ThemedLite>
      );
    }

    return this.props.children;
  }
}
